import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { BrandLogo } from "@/components/atoms";
import { ROUTES } from "@/constants";
import { useAuth, useTenant, useTenantBrand } from "@/hooks";
import {
  getBackofficeAreaHomeLink,
  resolveBackofficeArea,
} from "@/utils";
import "./BackofficeTopBar.scss";

interface BackofficeTopBarProps {
  title?: string;
}

/** Compact top bar for the backoffice shell with menu toggle and area home link. */
export const BackofficeTopBar: React.FC<BackofficeTopBarProps> = ({ title }) => {
  const { user } = useAuth();
  const { effectiveTenantSlug } = useTenant();
  const { getLogoSrc, logoAlt } = useTenantBrand();
  const { pathname } = useLocation();
  const area = resolveBackofficeArea(pathname);
  const homeLink = getBackofficeAreaHomeLink(area, effectiveTenantSlug);
  const logoSrc = getLogoSrc("navbar");

  return (
    <header className="backoffice-top-bar">
      <div className="backoffice-top-bar__section backoffice-top-bar__section--left">
        <Dialog.Trigger asChild>
          <button
            type="button"
            className="backoffice-top-bar__menu-trigger"
            aria-label="Abrir menú"
          >
            <Menu className="backoffice-top-bar__menu-icon" />
          </button>
        </Dialog.Trigger>
      </div>

      <div className="backoffice-top-bar__section backoffice-top-bar__section--center">
        {title && <h1 className="backoffice-top-bar__title">{title}</h1>}
      </div>

      <div className="backoffice-top-bar__section backoffice-top-bar__section--right">
        {homeLink.external ? (
          <a
            href={homeLink.href}
            className="backoffice-top-bar__link backoffice-top-bar__link--home"
            aria-label="Ir al inicio del panel"
          >
            <BrandLogo
              src={logoSrc}
              alt={logoAlt}
              className="backoffice-top-bar__logo"
              loading="eager"
            />
          </a>
        ) : (
          <Link
            to={user ? homeLink.href : ROUTES.backoffice.admin}
            className="backoffice-top-bar__link backoffice-top-bar__link--home"
            aria-label="Ir al inicio del panel"
          >
            <BrandLogo
              src={logoSrc}
              alt={logoAlt}
              className="backoffice-top-bar__logo"
              loading="eager"
            />
          </Link>
        )}
      </div>
    </header>
  );
};
